import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export interface FilterSummary {
  quarters?: string[];
  products?: string[];
  subProducts?: string[];
  statuses?: string[];
  classifications?: string[];
  tags?: string[];
  search?: string;
}

export interface ExportOptions {
  viewType: 'timeline' | 'list';
  format: 'png' | 'pdf';
  filterSummary: FilterSummary;
  initiativeCount: number;
}

// Threshold constants
export const EXPORT_WARNING_THRESHOLD = 100;
export const EXPORT_BLOCK_THRESHOLD = 300;

/**
 * Generates a timestamp string for file naming
 */
function getTimestamp(): string {
  const now = new Date();
  const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
  const time = now.toTimeString().split(' ')[0].replace(/:/g, ''); // HHMMSS
  return `${date}_${time}`;
}

/**
 * Formats filter summary for display in export header
 */
export function formatFilterSummary(filters: FilterSummary): string {
  const parts: string[] = [];

  if (filters.search) {
    parts.push(`Search: "${filters.search}"`);
  }
  if (filters.quarters && filters.quarters.length > 0) {
    parts.push(`Quarters: ${filters.quarters.join(', ')}`);
  }
  if (filters.products && filters.products.length > 0) {
    parts.push(`Product: ${filters.products.join(', ')}`);
  }
  if (filters.subProducts && filters.subProducts.length > 0) {
    parts.push(`Sub-product: ${filters.subProducts.join(', ')}`);
  }
  if (filters.statuses && filters.statuses.length > 0) {
    parts.push(`Status: ${filters.statuses.join(', ')}`);
  }
  if (filters.classifications && filters.classifications.length > 0) {
    parts.push(`Classification: ${filters.classifications.join(', ')}`);
  }
  if (filters.tags && filters.tags.length > 0) {
    parts.push(`Tags: ${filters.tags.join(', ')}`);
  }

  return parts.length > 0 ? parts.join(' | ') : 'None (showing all)';
}

/**
 * Creates the export header element with title, filters, and timestamp
 */
function createExportHeader(options: ExportOptions): HTMLDivElement {
  const header = document.createElement('div');
  header.style.cssText = `
    padding: 16px 24px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 4px;
  `;
  title.textContent = `CrossProd - ${options.viewType === 'timeline' ? 'Timeline' : 'List'} View`;

  const filterText = document.createElement('div');
  filterText.style.cssText = `
    font-size: 12px;
    color: #64748b;
    margin-bottom: 2px;
  `;
  filterText.textContent = `Filters: ${formatFilterSummary(options.filterSummary)}`;

  const timestamp = document.createElement('div');
  timestamp.style.cssText = `
    font-size: 12px;
    color: #94a3b8;
  `;
  timestamp.textContent = `Exported: ${new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;

  header.appendChild(title);
  header.appendChild(filterText);
  header.appendChild(timestamp);

  return header;
}

/**
 * Hides trailing empty quarter columns in timeline export
 * Finds the last quarter with initiatives and hides all columns after it
 */
function hideTrailingEmptyQuarters(clone: HTMLElement): void {
  // Find all grid rows (header + data rows)
  const gridRows = clone.querySelectorAll('[style*="grid-template-columns"]');
  if (gridRows.length === 0) return;

  // Get header row to count quarters
  const headerRow = gridRows[0] as HTMLElement;
  const headerCells = headerRow.children;
  if (headerCells.length <= 1) return; // Only product column, no quarters

  const totalQuarters = headerCells.length - 1; // Subtract product/initiative column

  // Find the last quarter index that has any initiative cards
  let lastQuarterWithData = -1;

  // Look for initiative cards and find their rightmost position
  const initiativeCards = clone.querySelectorAll('.absolute.box-border');
  initiativeCards.forEach((card) => {
    const htmlCard = card as HTMLElement;
    const left = parseFloat(htmlCard.style.left) || 0;
    const width = parseFloat(htmlCard.style.width) || 0;
    const rightEdge = left + width;
    // Each quarter is 200px wide
    const endQuarterIndex = Math.ceil(rightEdge / 200) - 1;
    if (endQuarterIndex > lastQuarterWithData) {
      lastQuarterWithData = endQuarterIndex;
    }
  });

  // If no initiatives found or all quarters have data, don't hide anything
  if (lastQuarterWithData < 0 || lastQuarterWithData >= totalQuarters - 1) return;

  // Calculate new grid template (keep first column + quarters up to lastQuarterWithData)
  const quartersToShow = lastQuarterWithData + 1;
  const newGridTemplate = `15rem repeat(${quartersToShow}, 200px)`;

  // Update all grid rows
  gridRows.forEach((row) => {
    const htmlRow = row as HTMLElement;
    htmlRow.style.gridTemplateColumns = newGridTemplate;

    // Hide quarter cells beyond lastQuarterWithData
    const cells = htmlRow.children;
    for (let i = quartersToShow + 1; i < cells.length; i++) {
      const cell = cells[i] as HTMLElement;
      cell.style.display = 'none';
    }
  });

  // Also update the timeline containers that span multiple columns
  const timelineContainers = clone.querySelectorAll('[style*="grid-column"]');
  timelineContainers.forEach((container) => {
    const htmlContainer = container as HTMLElement;
    const gridColumn = htmlContainer.style.gridColumn;
    if (gridColumn && gridColumn.includes('span')) {
      // Update span to only cover visible quarters
      htmlContainer.style.gridColumn = `2 / span ${quartersToShow}`;
    }
  });

  // Hide quarter dividers beyond the visible range
  const quarterDividers = clone.querySelectorAll('[style*="width: 200px"]');
  let dividerIndex = 0;
  quarterDividers.forEach((divider) => {
    const parent = divider.parentElement;
    if (parent && parent.style.pointerEvents === 'none') {
      // This is inside a quarter divider container
      if (dividerIndex >= quartersToShow) {
        (divider as HTMLElement).style.display = 'none';
      }
      dividerIndex++;
    }
  });
}

/**
 * Prepares the view container for export by:
 * 1. Cloning the container
 * 2. Expanding to full size (removing scroll)
 * 3. Fixing sticky elements
 * 4. Adding export header
 * 5. Removing truncation to show full titles
 */
async function prepareExportContainer(
  sourceElement: HTMLElement,
  options: ExportOptions
): Promise<HTMLDivElement> {
  // Create wrapper container
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    background: white;
    width: fit-content;
  `;

  // Add export header
  const header = createExportHeader(options);
  wrapper.appendChild(header);

  // Clone the source element
  const clone = sourceElement.cloneNode(true) as HTMLElement;

  // Remove overflow hidden to show full content
  clone.style.overflow = 'visible';
  clone.style.height = 'auto';
  clone.style.maxHeight = 'none';

  // Find and fix scrollable containers
  const scrollContainers = clone.querySelectorAll('[class*="overflow"]');
  scrollContainers.forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.overflow = 'visible';
    htmlEl.style.height = 'auto';
    htmlEl.style.maxHeight = 'none';
  });

  // Fix sticky elements - make them static
  const stickyElements = clone.querySelectorAll('[class*="sticky"]');
  stickyElements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.position = 'static';
  });

  // For timeline view, ensure min-width containers expand
  const minWidthContainers = clone.querySelectorAll('[class*="min-w-max"]');
  minWidthContainers.forEach((el) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.minWidth = 'auto';
    htmlEl.style.width = 'auto';
  });

  // Remove truncation to show full titles
  if (options.viewType === 'list') {
    // List View: Remove truncation and max-width constraints
    const truncatedElements = clone.querySelectorAll('.truncate');
    truncatedElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.overflow = 'visible';
      htmlEl.style.textOverflow = 'clip';
      htmlEl.style.whiteSpace = 'normal';
      htmlEl.style.wordBreak = 'break-word';
    });

    // Remove max-width constraints on title cells
    const maxWidthElements = clone.querySelectorAll('[class*="max-w-"]');
    maxWidthElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.maxWidth = 'none';
    });

    // Ensure table cells can expand
    const tableCells = clone.querySelectorAll('td');
    tableCells.forEach((cell) => {
      cell.style.whiteSpace = 'normal';
      cell.style.wordBreak = 'break-word';
    });
  } else {
    // Timeline View: Expand cards to show more title text (up to 3 lines)
    // Using simple approach: remove truncation, let text wrap, expand card height

    const EXPANDED_CARD_HEIGHT = 4.5; // rem - enough for ~3 lines
    const CARD_GAP = 0.5; // rem
    const ROW_PADDING = 0.75; // rem

    // Step 1: Find and expand all initiative card containers
    const cardContainers = clone.querySelectorAll('.absolute.box-border');
    cardContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement;
      // Set new height
      htmlContainer.style.height = `${EXPANDED_CARD_HEIGHT}rem`;
    });

    // Step 2: Modify the card div inside each container - change to vertical layout
    const allCards = clone.querySelectorAll('.absolute.box-border > div');
    allCards.forEach((card) => {
      const htmlCard = card as HTMLElement;
      if (htmlCard.className.includes('rounded-full') || htmlCard.className.includes('rounded') || htmlCard.className.includes('group')) {
        // Change pill to rounded rectangle
        htmlCard.style.borderRadius = '0.5rem';
        // Fill the container
        htmlCard.style.height = '100%';
        // Change to vertical flex layout with wrapping
        htmlCard.style.display = 'flex';
        htmlCard.style.flexDirection = 'row';
        htmlCard.style.flexWrap = 'wrap';
        htmlCard.style.alignItems = 'flex-start';
        htmlCard.style.alignContent = 'space-between';
        htmlCard.style.overflow = 'hidden';
        htmlCard.style.gap = '0.25rem';
        htmlCard.style.padding = '0.5rem 0.75rem';
      }
    });

    // Step 3: Make title span full width (forces other elements to wrap to next line)
    allCards.forEach((card) => {
      const htmlCard = card as HTMLElement;
      // Get the first span child - that's the title
      const titleSpan = htmlCard.querySelector(':scope > span');
      if (titleSpan) {
        const htmlTitle = titleSpan as HTMLElement;
        // Make title take full width to force wrap
        htmlTitle.style.width = '100%';
        htmlTitle.style.flex = '0 0 100%';
        htmlTitle.style.overflow = 'hidden';
        htmlTitle.style.textOverflow = 'clip';
        htmlTitle.style.whiteSpace = 'normal';
        htmlTitle.style.wordBreak = 'break-word';
        htmlTitle.style.lineHeight = '1.3';
        htmlTitle.style.marginBottom = '0.25rem';
      }

      // Make sure child divs (status, tags, icons) stay visible
      const childDivs = htmlCard.querySelectorAll(':scope > div');
      childDivs.forEach((div) => {
        const htmlDiv = div as HTMLElement;
        // Skip tooltip divs (they have absolute positioning)
        if (!htmlDiv.className.includes('absolute')) {
          htmlDiv.style.flexShrink = '0';
        }
      });
    });

    // Step 4: Recalculate row heights and card positions
    const gridRows = clone.querySelectorAll('[style*="grid-template-columns"]');
    gridRows.forEach((row) => {
      const htmlRow = row as HTMLElement;
      const currentHeight = htmlRow.style.height;

      // Only process rows with explicit height (sub-product rows with initiatives)
      if (currentHeight && currentHeight.includes('rem')) {
        const cards = htmlRow.querySelectorAll('.absolute.box-border');
        if (cards.length > 0) {
          // Calculate new row height
          const cardCount = cards.length;
          const newRowHeight = ROW_PADDING + (cardCount * (EXPANDED_CARD_HEIGHT + CARD_GAP));
          htmlRow.style.height = `${newRowHeight}rem`;

          // Update sub-product name cell
          const nameCell = htmlRow.querySelector('div[class*="sticky"][class*="left-0"]');
          if (nameCell) {
            (nameCell as HTMLElement).style.height = `${newRowHeight}rem`;
          }

          // Update timeline container
          const timelineContainer = htmlRow.querySelector('div[style*="grid-column"]');
          if (timelineContainer) {
            (timelineContainer as HTMLElement).style.height = `${newRowHeight}rem`;
          }

          // Reposition cards with new spacing
          cards.forEach((card, idx) => {
            const htmlCard = card as HTMLElement;
            const newTop = ROW_PADDING + (idx * (EXPANDED_CARD_HEIGHT + CARD_GAP));
            htmlCard.style.top = `${newTop}rem`;
          });
        }
      }
    });

    // Hide trailing empty quarter columns
    hideTrailingEmptyQuarters(clone);
  }

  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  // Wait for rendering
  await new Promise((resolve) => setTimeout(resolve, 100));

  return wrapper;
}

/**
 * Exports the view as PNG
 */
async function exportAsPNG(
  canvas: HTMLCanvasElement,
  viewType: 'timeline' | 'list'
): Promise<void> {
  const timestamp = getTimestamp();
  const filename = `${viewType}_${timestamp}.png`;

  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/**
 * Exports the view as PDF
 */
async function exportAsPDF(
  canvas: HTMLCanvasElement,
  viewType: 'timeline' | 'list'
): Promise<void> {
  const timestamp = getTimestamp();
  const filename = `${viewType}_${timestamp}.pdf`;

  const imgWidth = canvas.width;
  const imgHeight = canvas.height;

  // Determine orientation based on aspect ratio
  const orientation = imgWidth > imgHeight ? 'landscape' : 'portrait';

  // Create PDF with custom size matching the canvas
  const pdf = new jsPDF({
    orientation,
    unit: 'px',
    format: [imgWidth / 2, imgHeight / 2], // Scale down for reasonable file size
  });

  // Add image to PDF
  const imgData = canvas.toDataURL('image/png');
  pdf.addImage(imgData, 'PNG', 0, 0, imgWidth / 2, imgHeight / 2);

  pdf.save(filename);
}

/**
 * Main export function
 */
export async function exportView(
  sourceElement: HTMLElement,
  options: ExportOptions
): Promise<{ success: boolean; error?: string }> {
  let exportContainer: HTMLDivElement | null = null;

  try {
    // Prepare the container for export
    exportContainer = await prepareExportContainer(sourceElement, options);

    // Capture with html2canvas
    const canvas = await html2canvas(exportContainer, {
      scale: 2, // Higher quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: exportContainer.scrollWidth,
      windowHeight: exportContainer.scrollHeight,
    });

    // Export based on format
    if (options.format === 'png') {
      await exportAsPNG(canvas, options.viewType);
    } else {
      await exportAsPDF(canvas, options.viewType);
    }

    return { success: true };
  } catch (error) {
    console.error('Export failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Export failed',
    };
  } finally {
    // Cleanup
    if (exportContainer && exportContainer.parentNode) {
      exportContainer.parentNode.removeChild(exportContainer);
    }
  }
}
